"use strict";
/**
 * Rule Types for Feature Flag Targeting (Data Layer)
 *
 * This module defines the DATA layer for feature flag evaluation.
 * All evaluation logic is defined here as JSON-serializable data structures
 * that can be stored in Firestore and manipulated via admin UI.
 *
 * IMPORTANT: This is the DATA layer - it defines:
 * - Conditions (attribute, segment, flag comparisons)
 * - Targeting rules (condition → value mappings)
 * - Segment definitions (reusable user groups)
 * - Flag programs (complete evaluation logic for a flag)
 * - Explicit input dependencies (watch set for recomputation)
 *
 * The code layer (definitions.ts) only defines flag types/schemas.
 * This layer defines HOW flags are evaluated.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FlagConfigSchema = exports.FlagProgramSchema = exports.SegmentDefinitionSchema = exports.InputDependenciesSchema = exports.TargetingRuleSchema = exports.ConditionSchema = exports.OrConditionSchema = exports.AndConditionSchema = exports.BaseConditionSchema = exports.FlagConditionSchema = exports.SegmentConditionSchema = exports.AttributeConditionSchema = exports.ComparisonOperator = void 0;
exports.attributeEquals = attributeEquals;
exports.emailEndsWith = emailEndsWith;
exports.inSegment = inSegment;
exports.flagEquals = flagEquals;
exports.and = and;
exports.or = or;
const zod_1 = require("zod");
/**
 * Comparison operators for rule conditions
 */
exports.ComparisonOperator = zod_1.z.enum([
    "equals",
    "notEquals",
    "contains",
    "notContains",
    "startsWith",
    "endsWith",
    "greaterThan",
    "greaterThanOrEquals",
    "lessThan",
    "lessThanOrEquals",
    "in",
    "notIn",
    "matches", // regex match
]);
/**
 * Attribute condition - compares a context attribute to a value
 */
exports.AttributeConditionSchema = zod_1.z.object({
    type: zod_1.z.literal("attribute"),
    /**
     * Path to the attribute in the context
     * Examples: "user.email", "user.planEpoch", "system.environment"
     */
    attribute: zod_1.z.string(),
    /**
     * Comparison operator
     */
    operator: exports.ComparisonOperator,
    /**
     * Value to compare against
     */
    value: zod_1.z.unknown(),
});
/**
 * Segment condition - checks if user is in a segment
 */
exports.SegmentConditionSchema = zod_1.z.object({
    type: zod_1.z.literal("segment"),
    /**
     * Segment ID to check membership
     */
    segmentId: zod_1.z.string(),
    /**
     * Whether to negate the condition (user NOT in segment)
     */
    negated: zod_1.z.boolean(),
});
/**
 * Flag condition - checks the value of another flag
 * This enables DAG-based flag dependencies
 */
exports.FlagConditionSchema = zod_1.z.object({
    type: zod_1.z.literal("flag"),
    /**
     * The flag key to check
     */
    flagKey: zod_1.z.string(),
    /**
     * Comparison operator
     */
    operator: exports.ComparisonOperator,
    /**
     * Value to compare against
     */
    value: zod_1.z.unknown(),
});
/**
 * Base condition - union of all condition types
 */
exports.BaseConditionSchema = zod_1.z.discriminatedUnion("type", [
    exports.AttributeConditionSchema,
    exports.SegmentConditionSchema,
    exports.FlagConditionSchema,
]);
/**
 * Logical AND condition - all conditions must be true
 */
exports.AndConditionSchema = zod_1.z.object({
    type: zod_1.z.literal("and"),
    conditions: zod_1.z.lazy(() => zod_1.z.array(exports.ConditionSchema)),
});
/**
 * Logical OR condition - at least one condition must be true
 */
exports.OrConditionSchema = zod_1.z.object({
    type: zod_1.z.literal("or"),
    conditions: zod_1.z.lazy(() => zod_1.z.array(exports.ConditionSchema)),
});
/**
 * Union of all condition types including logical operators
 */
exports.ConditionSchema = zod_1.z.lazy(() => zod_1.z.union([exports.BaseConditionSchema, exports.AndConditionSchema, exports.OrConditionSchema]));
/**
 * A targeting rule - if conditions match, return the specified value
 *
 * The Zod schema uses z.unknown() for the value field because it needs to
 * handle deserialization from Firestore where the type is not known at parse time.
 * For type-safe usage, use TypedTargetingRule<T> instead.
 */
exports.TargetingRuleSchema = zod_1.z.object({
    /**
     * Unique identifier for this rule
     */
    id: zod_1.z.string(),
    /**
     * Human-readable description of what this rule does
     */
    description: zod_1.z.string().optional(),
    /**
     * Conditions that must be met for this rule to apply
     */
    condition: exports.ConditionSchema,
    /**
     * The value to return if conditions are met
     */
    value: zod_1.z.unknown(),
    /**
     * Whether this rule is enabled
     */
    enabled: zod_1.z.boolean(),
});
/**
 * Input dependencies schema - explicit declaration of what inputs a program reads
 *
 * This is the "watch set" - when any of these inputs change, the program needs
 * to be re-evaluated. This must be explicitly declared and validated against
 * what the conditions actually reference.
 */
exports.InputDependenciesSchema = zod_1.z.object({
    /**
     * User context attribute paths that this program reads
     * Examples: ["user.email", "user.planEpoch", "user.customAttributes.isBetaTester"]
     */
    userAttributes: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * System context attribute paths that this program reads
     * Examples: ["system.environment", "system.customAttributes.maintenanceMode"]
     */
    systemAttributes: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * Segment IDs that this program checks membership for
     * Examples: ["internal-users", "beta-testers"]
     */
    segments: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * Other flag keys that this program depends on (for DAG evaluation)
     * Examples: ["beta-features-enabled", "pcb-auto-layout"]
     */
    flags: zod_1.z.array(zod_1.z.string()).default([]),
});
/**
 * Segment definition - a reusable group of users (DATA layer)
 *
 * Segments are defined in Firestore and manipulated via admin UI.
 * They include explicit input dependencies for the watch set.
 */
exports.SegmentDefinitionSchema = zod_1.z.object({
    /**
     * Unique identifier for this segment
     */
    id: zod_1.z.string(),
    /**
     * Human-readable name
     */
    name: zod_1.z.string(),
    /**
     * Description of what this segment represents
     */
    description: zod_1.z.string().optional(),
    /**
     * Conditions for segment membership
     */
    condition: exports.ConditionSchema,
    /**
     * Explicit input dependencies - what this segment reads
     * Must match what the condition actually references.
     */
    inputs: exports.InputDependenciesSchema,
    /**
     * When this segment was last updated
     */
    updatedAt: zod_1.z.number(),
    /**
     * Who last updated this segment
     */
    updatedBy: zod_1.z.string().optional(),
});
/**
 * Flag program - complete evaluation logic for a flag (DATA layer)
 *
 * This is the "program" that the evaluation engine interprets.
 * It contains all the logic for determining a flag's value for a user.
 * Stored in Firestore and manipulated via admin UI.
 *
 * IMPORTANT: All evaluation logic lives here, NOT in FlagDefinition (code layer).
 *
 * Note: Environment is NOT stored in the program - it's an input to evaluation.
 * Flag programs are global and environment-specific behavior is expressed via
 * targeting rules that check system.environment.
 */
exports.FlagProgramSchema = zod_1.z.object({
    /**
     * The flag key this program is for (must match a FlagDefinition)
     */
    flagKey: zod_1.z.string(),
    /**
     * Whether this flag is enabled (if false, returns default value)
     */
    enabled: zod_1.z.boolean(),
    /**
     * Default value for this flag in this environment
     * This is the value returned if no rules match and no rollout applies.
     */
    defaultValue: zod_1.z.unknown(),
    /**
     * Targeting rules in precedence order (first match wins)
     */
    rules: zod_1.z.array(exports.TargetingRuleSchema).default([]),
    /**
     * Rollout percentage (0-100) for gradual rollouts
     * Applied after rules, before default
     */
    rolloutPercentage: zod_1.z.number().min(0).max(100).optional(),
    /**
     * Value to use for users in the rollout
     */
    rolloutValue: zod_1.z.unknown().optional(),
    /**
     * Explicit input dependencies - what this program reads
     * This is the "watch set" for recomputation.
     * Must match what the rules actually reference.
     */
    inputs: exports.InputDependenciesSchema,
    /**
     * When this program was last updated
     */
    updatedAt: zod_1.z.number(),
    /**
     * Who last updated this program
     */
    updatedBy: zod_1.z.string().optional(),
});
/**
 * @deprecated Use FlagProgram instead. FlagConfig is kept for backwards compatibility.
 */
exports.FlagConfigSchema = exports.FlagProgramSchema;
/**
 * Helper to create a simple attribute condition
 */
function attributeEquals(attribute, value) {
    return {
        type: "attribute",
        attribute,
        operator: "equals",
        value,
    };
}
/**
 * Helper to create an email domain condition
 */
function emailEndsWith(domain) {
    return {
        type: "attribute",
        attribute: "user.email",
        operator: "endsWith",
        value: domain,
    };
}
/**
 * Helper to create a segment membership condition
 */
function inSegment(segmentId) {
    return {
        type: "segment",
        segmentId,
        negated: false,
    };
}
/**
 * Helper to create a flag dependency condition
 */
function flagEquals(flagKey, value) {
    return {
        type: "flag",
        flagKey,
        operator: "equals",
        value,
    };
}
/**
 * Helper to combine conditions with AND
 */
function and(...conditions) {
    return {
        type: "and",
        conditions,
    };
}
/**
 * Helper to combine conditions with OR
 */
function or(...conditions) {
    return {
        type: "or",
        conditions,
    };
}
//# sourceMappingURL=rules.js.map